import React, {
  useState,
  useRef,
  useEffect,
  useMemo,
  useCallback,
} from "react";
import { Event, Agent, Workflow, AGENT_COLORS } from "@/shared/types";
import { MarkdownRenderer } from "./MarkdownRenderer";
import { ToolSelector } from "./ToolSelector";

interface ChatMessage {
  id: string;
  agentId: string;
  agentType: string;
  content: string;
  timestamp: number;
  type: string;
  isUser?: boolean;
}

interface ChatClientProps {
  events: Event[];
  agents: Agent[];
  className?: string;
  onSendMessage?: (_message: string, _targetAgents: string[]) => void;
  onMessageSelect?: (_eventId: string) => void;
  sessions?: Workflow[];
  onLoadSession?: (_workflowId: string) => void;
  onNewChat?: () => void;
  isLoadingSessions?: boolean;
}

export const ChatClient: React.FC<ChatClientProps> = ({
  events,
  agents,
  className = "",
  onSendMessage,
  onMessageSelect,
  sessions,
  onLoadSession,
  onNewChat,
  isLoadingSessions,
}) => {
  const [selectedAgents, setSelectedAgents] = useState<Set<string>>(new Set());
  const [inputMessage, setInputMessage] = useState("");
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [activeTab, setActiveTab] = useState<"chat" | "artifacts">("chat");
  const [autoScroll, setAutoScroll] = useState(true);
  const [selectedMessageId, setSelectedMessageId] = useState<string | null>(
    null,
  );
  const [showToolSelector, setShowToolSelector] = useState(false);
  const [enabledTools, setEnabledTools] = useState<string[]>([]);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);

  // Convert events to chat messages and filter by selected agents
  const chatMessages = useMemo(() => {
    const messages: ChatMessage[] = events
      .filter(
        (event) =>
          event.type === "message" ||
          event.type === "toolCall" ||
          event.type === "toolResult",
      )
      .map((event) => {
        const agent = agents.find((a) => a.id === event.agentId);
        const isUserMessage = event.metadata?.from === "user";
        return {
          id: event.id,
          agentId: event.agentId,
          agentType: isUserMessage ? "user" : (agent?.type || "unknown"),
          content: event.content,
          timestamp: event.timestamp,
          type: event.type,
          isUser: isUserMessage,
        };
      })
      .sort((a, b) => a.timestamp - b.timestamp);

    // Filter by selected agents if any are selected
    if (selectedAgents.size > 0) {
      return messages.filter((msg) => selectedAgents.has(msg.agentId));
    }

    return messages;
  }, [events, agents, selectedAgents]);

  const artifacts = useMemo(() => {
    return events
      .filter((e) => e.type === "artifact")
      .map((e) => ({
        id: e.id,
        agentId: e.agentId,
        title: (e.metadata?.title as string) || "Untitled Artifact",
        kind: (e.metadata?.kind as string) || "markdown",
        filename: (e.metadata?.filename as string) || undefined,
        content: e.content,
        timestamp: e.timestamp,
      }))
      .sort((a, b) => a.timestamp - b.timestamp);
  }, [events]);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    if (autoScroll && messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [chatMessages, autoScroll]);

  // Handle agent selection toggle
  const toggleAgent = useCallback((agentId: string) => {
    setSelectedAgents((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(agentId)) {
        newSet.delete(agentId);
      } else {
        newSet.add(agentId);
      }
      return newSet;
    });
  }, []);

  // Select all agents
  const selectAllAgents = useCallback(() => {
    setSelectedAgents(new Set(agents.map((a) => a.id)));
  }, [agents]);

  // Clear agent selection
  const clearSelection = useCallback(() => {
    setSelectedAgents(new Set());
  }, []);

  // Handle send message
  const handleSendMessage = useCallback(() => {
    if (!inputMessage.trim()) return;

    const targetAgents =
      selectedAgents.size > 0
        ? Array.from(selectedAgents)
        : agents.map((a) => a.id);

    onSendMessage?.(inputMessage.trim(), targetAgents);
    setInputMessage("");
  }, [inputMessage, selectedAgents, agents, onSendMessage]);

  // Handle key press in input
  const handleKeyPress = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        handleSendMessage();
      }
    },
    [handleSendMessage],
  );

  // Handle scroll to detect if user scrolled up (disable auto-scroll)
  const handleScroll = useCallback(() => {
    if (!messagesContainerRef.current) return;

    const { scrollTop, scrollHeight, clientHeight } =
      messagesContainerRef.current;
    const isAtBottom = scrollHeight - scrollTop - clientHeight < 10;

    if (autoScroll && !isAtBottom) {
      setAutoScroll(false);
    } else if (!autoScroll && isAtBottom) {
      setAutoScroll(true);
    }
  }, [autoScroll]);

  // Format timestamp for display
  const formatTimestamp = useCallback((timestamp: number) => {
    const date = new Date(timestamp * 1000);
    return date.toLocaleTimeString("en-US", {
      hour12: false,
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  }, []);

  // Get agent status count for display
  const agentMessageCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    chatMessages.forEach((msg) => {
      counts[msg.agentId] = (counts[msg.agentId] || 0) + 1;
    });
    return counts;
  }, [chatMessages]);

  // Handle message selection
  const handleMessageSelect = useCallback(
    (messageId: string, eventId: string) => {
      setSelectedMessageId(messageId);
      onMessageSelect?.(eventId);
    },
    [onMessageSelect],
  );

  if (isCollapsed) {
    return (
      <div className={`flex flex-col ${className}`}>
        <div className="bg-gray-800 border-b border-gray-700 p-2">
          <button
            onClick={() => setIsCollapsed(false)}
            className="w-full flex items-center justify-between text-sm text-gray-300 hover:text-white transition-colors"
          >
            <span>Chat ({chatMessages.length} messages)</span>
            <svg
              className="w-4 h-4 transform rotate-180"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                clipRule="evenodd"
              />
            </svg>
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={`flex flex-col h-full ${className}`}>
      {/* Header */}
      <div className="bg-gray-800 border-b border-gray-700 p-3">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <button
              onClick={() => setActiveTab("chat")}
              className={`text-sm px-2 py-1 rounded ${
                activeTab === "chat" ? "bg-blue-600 text-white" : "text-gray-300 hover:text-white"
              }`}
            >
              Chat
            </button>
            <button
              onClick={() => setActiveTab("artifacts")}
              className={`text-sm px-2 py-1 rounded ${
                activeTab === "artifacts" ? "bg-blue-600 text-white" : "text-gray-300 hover:text-white"
              }`}
            >
              Artifacts
            </button>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowToolSelector(!showToolSelector)}
              className={`px-2 py-1 text-xs rounded flex items-center gap-1 ${
                showToolSelector 
                  ? 'bg-purple-600 hover:bg-purple-700 text-white' 
                  : 'bg-gray-600 hover:bg-gray-700 text-gray-300'
              }`}
              title="Manage available tools"
            >
              <span>🔧</span>
              Tools ({enabledTools.length})
            </button>
            {onNewChat && (
              <button
                onClick={onNewChat}
                className="px-2 py-1 text-xs rounded bg-green-600 hover:bg-green-700 text-white"
                title="Start a new chat session"
              >
                New Chat
              </button>
            )}
            <div className="relative">
              <select
                className="bg-gray-700 text-gray-200 text-xs px-2 py-1 rounded border border-gray-600 pr-6"
                onChange={(e) => e.target.value && onLoadSession?.(e.target.value)}
                defaultValue=""
                disabled={!sessions || sessions.length === 0}
                title="Load a previous chat session"
              >
                <option value="" disabled>
                  {isLoadingSessions ? "Loading sessions..." : "Load session"}
                </option>
                {(sessions || []).map((wf) => (
                  <option key={String(wf.id)} value={String(wf.id)}>
                    {wf.name || wf.id}
                  </option>
                ))}
              </select>
            </div>
            <button
              onClick={() => setAutoScroll(!autoScroll)}
              className={`px-2 py-1 text-xs rounded ${
                autoScroll
                  ? "bg-blue-600 text-white"
                  : "bg-gray-600 text-gray-300"
              }`}
              title={autoScroll ? "Disable auto-scroll" : "Enable auto-scroll"}
            >
              Auto
            </button>
            <button
              onClick={() => setIsCollapsed(true)}
              className="text-gray-400 hover:text-white transition-colors"
            >
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                  clipRule="evenodd"
                />
              </svg>
            </button>
          </div>
        </div>

        {/* Agent filters */}
        <div className="space-y-2">
          <div className="flex items-center gap-2 text-xs">
            <span className="text-gray-400">Filter agents:</span>
            <button
              onClick={selectAllAgents}
              className="text-blue-400 hover:text-blue-300 transition-colors"
            >
              All
            </button>
            <button
              onClick={clearSelection}
              className="text-red-400 hover:text-red-300 transition-colors"
            >
              None
            </button>
          </div>

          <div className="flex flex-wrap gap-1">
            {agents.map((agent) => {
              const isSelected = selectedAgents.has(agent.id);
              const messageCount = agentMessageCounts[agent.id] || 0;
              const agentColor = AGENT_COLORS[agent.type] || "#6b7280";

              return (
                <button
                  key={agent.id}
                  onClick={() => toggleAgent(agent.id)}
                  className={`
                    px-2 py-1 text-xs rounded flex items-center gap-1 transition-all duration-200
                    ${
                      isSelected
                        ? "text-white shadow-md"
                        : "bg-gray-700 text-gray-300 hover:bg-gray-600"
                    }
                  `}
                  style={{
                    backgroundColor: isSelected ? agentColor : undefined,
                  }}
                  title={`${agent.type} (${messageCount} messages)`}
                >
                  <div
                    className="w-2 h-2 rounded-full"
                    style={{ backgroundColor: agentColor }}
                  />
                  <span>{agent.type}</span>
                  {messageCount > 0 && (
                    <span className="bg-black/20 px-1 rounded-full text-[10px]">
                      {messageCount}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* Tool Selector */}
        {showToolSelector && (
          <div className="mt-3 border-t border-gray-700 pt-3">
            <ToolSelector
              onToolsChange={setEnabledTools}
              className="max-h-64"
            />
          </div>
        )}
      </div>

      {/* Body */}
      {activeTab === "chat" ? (
        <div
          ref={messagesContainerRef}
          className="flex-1 overflow-y-auto p-3 space-y-3"
          onScroll={handleScroll}
        >
          {chatMessages.length === 0 ? (
            <div className="text-center text-gray-500 mt-8">
              <div className="text-4xl mb-2">💬</div>
              <div className="text-sm">No messages to display</div>
              {selectedAgents.size > 0 && (
                <div className="text-xs mt-1">
                  Showing messages from {selectedAgents.size} selected agent(s)
                </div>
              )}
            </div>
          ) : (
            <>
              {chatMessages.map((message) => {
                const isUserMessage = message.isUser;
                const agentColor = isUserMessage 
                  ? "#3B82F6" // Blue for user messages
                  : AGENT_COLORS[message.agentType as keyof typeof AGENT_COLORS] ||
                    "#6b7280";

                const isSelected = selectedMessageId === message.id;

                return (
                  <div
                    key={message.id}
                    className={`flex gap-3 p-2 rounded-lg cursor-pointer transition-all duration-200 relative ${
                      isSelected
                        ? "bg-blue-600/20 border border-blue-500/50 shadow-lg"
                        : isUserMessage
                          ? "hover:bg-blue-600/10"
                          : "hover:bg-gray-700/30"
                    } ${isUserMessage ? "flex-row-reverse" : ""}`}
                    onClick={() => handleMessageSelect(message.id, message.id)}
                    title="Click to center this message in the waterfall diagram"
                  >
                    {isSelected && (
                      <div className={`absolute top-1/2 -translate-y-1/2 w-1 h-8 bg-blue-500 rounded-r ${isUserMessage ? "right-0 rounded-l" : "left-0"}`}></div>
                    )}
                    <div
                      className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-medium flex-shrink-0"
                      style={{ backgroundColor: agentColor }}
                    >
                      {isUserMessage ? "👤" : message.agentType.charAt(0).toUpperCase()}
                    </div>
                    <div className={`flex-1 relative ${isUserMessage ? "text-right" : ""}`}>
                      <div className={`flex items-center gap-2 mb-1 ${isUserMessage ? "justify-end" : ""}`}>
                        <span className="text-sm font-medium text-white flex items-center gap-1">
                          {isUserMessage ? "You" : message.agentType}
                        </span>
                        <span className="text-xs text-gray-400">
                          {formatTimestamp(message.timestamp)}
                        </span>
                        {!isUserMessage && (
                          <span
                            className={`
                            text-xs px-1.5 py-0.5 rounded-full
                            ${
                              message.type === "message"
                                ? "bg-gray-600 text-gray-300"
                                : message.type === "toolCall"
                                  ? "bg-blue-600 text-blue-200"
                                  : "bg-green-600 text-green-200"
                            }
                          `}
                          >
                            {message.type}
                          </span>
                        )}
                      </div>
                      <div
                        className={`text-sm text-gray-200 rounded-lg p-3 break-words overflow-wrap-anywhere whitespace-pre-wrap ${
                          isSelected 
                            ? "bg-blue-600/30" 
                            : isUserMessage 
                              ? "bg-blue-600/20 border border-blue-500/30" 
                              : "bg-gray-700/50"
                        }`}
                        style={{ wordBreak: 'break-word', overflowWrap: 'anywhere' }}
                      >
                        <MarkdownRenderer content={message.content} compact={false} />
                      </div>
                    </div>
                  </div>
                );
              })}
              <div ref={messagesEndRef} />
            </>
          )}
        </div>
      ) : (
        <div className="flex-1 overflow-y-auto p-3 space-y-3">
          {artifacts.length === 0 ? (
            <div className="text-center text-gray-500 mt-8">
              <div className="text-4xl mb-2">📄</div>
              <div className="text-sm">No artifacts yet</div>
            </div>
          ) : (
            artifacts.map((a) => (
              <div key={a.id} className="bg-gray-800 border border-gray-700 rounded p-3">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-sm text-white font-medium">{a.title}</div>
                  <div className="text-xs text-gray-400">{a.kind}</div>
                </div>
                {a.kind === "markdown" ? (
                  <MarkdownRenderer content={a.content} compact={false} />
                ) : (
                  <pre className="text-xs bg-black/40 p-2 rounded overflow-auto"><code>{a.content}</code></pre>
                )}
                {a.filename && (
                  <div className="text-xs text-gray-500 mt-2">{a.filename}</div>
                )}
              </div>
            ))
          )}
        </div>
      )}

      {/* Input area */}
      <div className="border-t border-gray-700 p-3 bg-gray-800">
        <div className="flex gap-2">
          <div className="flex-1">
            <textarea
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder={
                selectedAgents.size > 0
                  ? `Message ${selectedAgents.size} selected agent(s)...`
                  : "Message all agents..."
              }
              className="w-full p-2 text-sm bg-gray-700 text-white rounded-lg border border-gray-600 focus:border-blue-500 focus:outline-none resize-none"
              rows={2}
              disabled={!onSendMessage}
            />
          </div>
          <button
            onClick={handleSendMessage}
            disabled={!inputMessage.trim() || !onSendMessage}
            className={`
              px-4 py-2 rounded-lg text-sm font-medium transition-colors flex-shrink-0
              ${
                inputMessage.trim() && onSendMessage
                  ? "bg-blue-600 text-white hover:bg-blue-700"
                  : "bg-gray-600 text-gray-400 cursor-not-allowed"
              }
            `}
          >
            Send
          </button>
        </div>

        {selectedAgents.size > 0 && (
          <div className="mt-2 text-xs text-gray-400">
            Targeting:{" "}
            {Array.from(selectedAgents)
              .map((id) => agents.find((a) => a.id === id)?.type)
              .filter(Boolean)
              .join(", ")}
          </div>
        )}
      </div>
    </div>
  );
};
